// client/src/components/Login.js

import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance'; // Use axiosInstance for all requests
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import InterCollectIcon from '../Icons/InterCollect.png';

function Login() {
  const { featureFlags } = useFeatureFlags();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');

  // MFA flow state
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaUserId, setMfaUserId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');

  // 'loading' | 'needed' | 'not-needed' | 'error'
  const [setupStatus, setSetupStatus] = useState('loading');

  // Redirect to first-run setup if no users exist yet
  useEffect(() => {
    axiosInstance.get('/auth/setup-required')
      .then(res => {
        if (res.data.required === true) {
          setSetupStatus('needed');
          window.location.href = '/setup';
        } else {
          setSetupStatus('not-needed');
        }
      })
      .catch(() => {
        // Backend unreachable — surface a visible hint so the user can navigate manually
        setSetupStatus('error');
      });
  }, []);

  // Fetch the background image on component mount and when the page is refreshed
  useEffect(() => {
    const fetchBackground = async () => {
      try {
        // Add a cache-busting query parameter with current timestamp
        const timestamp = new Date().getTime();
        const response = await axiosInstance.get(`/random-background?t=${timestamp}`);
        if (response.data && response.data.imageUrl) {
          setBackgroundImage(response.data.imageUrl);
        } else {
          console.error('No background image URL received');
        }
      } catch (error) {
        console.error('Error fetching random background:', error.message);
      }
    };

    fetchBackground();

    // Add event listener for page visibility changes to refresh background when returning to the page
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchBackground();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Attempting login with:', { email });
      const response = await axiosInstance.post('/auth/login', { email, password });
      console.log('Login response:', response.data);

      // Check if MFA is required
      if (response.data.mfaPending) {
        setMfaUserId(response.data.userId || '');
        setMfaPending(true);
        return;
      }

      const { token, isAdmin } = response.data;

      if (!token) {
        throw new Error('No token received from server');
      }

      // Clear any existing tokens first
      localStorage.removeItem('token');
      localStorage.removeItem('isAdmin');

      // Save token and admin flag
      localStorage.setItem('token', token);
      localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');

      console.log('Token stored successfully:', token.substring(0, 10) + '...');

      // Ensure the token is set in the axios instance before navigation
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Force a reload of the page to ensure all auth states are properly initialized
      window.location.href = '/';

    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      if (error.response?.status === 429) {
        setError('Too many login attempts. Please wait an hour and try again.');
      } else {
        setError(error.response?.data?.error || 'Invalid email or password');
      }
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post('/mfa/verify', { userId: mfaUserId, code: mfaCode });
      localStorage.setItem('token', response.data.token);
      if (response.data.isAdmin !== undefined) {
        localStorage.setItem('isAdmin', response.data.isAdmin);
      }
      window.location.href = '/';
    } catch (err) {
      setMfaError(err.response?.data?.message || 'Invalid code. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In — InterCollect</title>
      </Helmet>
      <Container
        maxWidth={false}
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: 0,
          padding: 0,
          overflow: 'hidden',
        }}
      >
      <Box
        sx={{
          marginTop: 8,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: 4,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            marginBottom: 2,
          }}
        >
          <img
            src={InterCollectIcon}
            alt="InterCollect"
            style={{ height: 40 }}
          />
          <Typography variant="h4" component="h1">
            Login
          </Typography>
        </Box>
        {mfaPending ? (
          <Box component="form" onSubmit={handleMfaSubmit}>
            <Typography variant="h6" gutterBottom>Two-Factor Authentication</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter the 6-digit code from your authenticator app, or a backup code.
            </Typography>
            {mfaError && <Alert severity="error" sx={{ mb: 2 }}>{mfaError}</Alert>}
            <TextField
              fullWidth
              label="Authentication Code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              inputProps={{ maxLength: 12 }}
              sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth>Verify</Button>
            <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => setMfaPending(false)}>
              Back to Login
            </Button>
          </Box>
        ) : (
          <>
            {error && <Alert severity="error">{error}</Alert>}
            <form onSubmit={handleSubmit}>
              <TextField
                label="Email"
                variant="outlined"
                margin="normal"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="Password"
                variant="outlined"
                margin="normal"
                fullWidth
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Box sx={{ textAlign: 'right', mb: 1 }}>
                <Button component={Link} to="/forgot-password" variant="text" size="small">
                  Forgot password?
                </Button>
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<LockIcon />}
                sx={{ marginTop: 2 }}
              >
                Login
              </Button>
            </form>
            <Typography variant="body2" sx={{ marginTop: 2 }}>
              Don't have an account? <Link to="/register">Register here</Link>
            </Typography>
            {setupStatus === 'error' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Cannot reach the server. If this is a first-run setup,{' '}
                <Link to="/setup">click here to create your admin account</Link>.
              </Alert>
            )}
            {setupStatus === 'needed' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Redirecting to first-run setup…{' '}
                <Link to="/setup">Click here if it doesn't happen automatically</Link>.
              </Alert>
            )}
          {featureFlags.enableDiscovery && (
            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              <Link to="/about">What is InterCollect?</Link>
            </Typography>
          )}
          {featureFlags.enableDistribution && (
            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              <Link to="/download">Deploy your own instance</Link>
            </Typography>
          )}
          </>
        )}
      </Box>
    </Container>
    </>
  );
}

export default Login;
